import { useEffect, useLayoutEffect, useRef } from 'react';
import type { RefObject } from 'react';
import type { MapRef } from 'react-map-gl/maplibre';
import type maplibregl from 'maplibre-gl';
import type { FeatureCollection } from 'geojson';

import type { Client } from '@/clients/types';
import type { FeatureOverlayState } from '@/components/map/utils/mapLayers';
import { addOrbitLayer, ORBIT_LAYER_IDS } from '@/components/map/utils/mapLayerOrder';
import { fetchPipelinesForViewport } from '@/api/pipelinesApi';
import { attachViewportLoader, EMPTY_FEATURE_COLLECTION, setGeoJsonSourceData } from '@/utils/mapUtils';

export const PIPELINE_SOURCE_ID = 'orbit-pipelines-source';

// Pipelines first appear at the Pipelines semantic band start (zoom 16), so the source loads from
// zoom 16 — comfortably under the backend's 2-square-degree bbox limit (~zoom 9).
const PIPELINES_MIN_ZOOM = 16;

function registerPipelineSourceAndLayers(mlMap: maplibregl.Map, data: FeatureCollection, visible: boolean): void {
    const visibility = visible ? 'visible' : 'none';

    try {
        if (mlMap.getSource(PIPELINE_SOURCE_ID)) {
            (mlMap.getSource(PIPELINE_SOURCE_ID) as maplibregl.GeoJSONSource).setData(data);
        } else {
            // promoteId exposes each pipe's `id` as the feature id so the hover-grow tween
            // can target it via setFeatureState (see usePipelineInteractions).
            mlMap.addSource(PIPELINE_SOURCE_ID, { type: 'geojson', data, promoteId: 'id' });
        }
    } catch {
        return;
    }

    if (!mlMap.getLayer(ORBIT_LAYER_IDS.pipelinesCase)) {
        try {
            addOrbitLayer(
                mlMap,
                {
                    id: ORBIT_LAYER_IDS.pipelinesCase,
                    type: 'line',
                    source: PIPELINE_SOURCE_ID,
                    minzoom: PIPELINES_MIN_ZOOM,
                    layout: { visibility, 'line-cap': 'round', 'line-join': 'round' },
                    paint: {
                        // Drop shadow for the main pipeline: an offset dark line beneath it.
                        // Kept 4px wider than the main line (tracks the same zoom ramp and hover-grow
                        // factor) so the offset shadow stays visible. Registered first → renders
                        // underneath `pipelines`.
                        'line-color': '#000000',
                        'line-width': [
                            'interpolate',
                            ['linear'],
                            ['zoom'],
                            16,
                            ['+', ['*', 4, ['+', 1, ['number', ['feature-state', 'hoverProgress'], 0]]], 4],
                            17,
                            ['+', ['*', 8, ['+', 1, ['number', ['feature-state', 'hoverProgress'], 0]]], 4],
                        ],
                        'line-opacity': 0.1,
                        'line-blur': 2,
                        // Offset in screen pixels (viewport anchor) so the shadow keeps a constant
                        // drop direction regardless of map zoom/rotation.
                        'line-translate': [3, 3],
                        'line-translate-anchor': 'viewport',
                    },
                },
                'lineOverlays',
            );
        } catch {
            // Progressive enhancement — layer failures must not break the map.
        }
    }

    if (!mlMap.getLayer(ORBIT_LAYER_IDS.pipelines)) {
        try {
            // Pipelines are coloured by net_func type to match the External portal: distribution
            // mains teal, trunk mains orange and communication pipes amber; any other type
            // (SUPPLY / PRIVATE / DISCHARGE (ETS) / unknown) falls back to a light blue. Width and
            // the drop shadow (the pipelinesCase layer above) are the same for every type.
            addOrbitLayer(
                mlMap,
                {
                    id: ORBIT_LAYER_IDS.pipelines,
                    type: 'line',
                    source: PIPELINE_SOURCE_ID,
                    minzoom: PIPELINES_MIN_ZOOM,
                    layout: { visibility, 'line-cap': 'round', 'line-join': 'round' },
                    paint: {
                        'line-color': [
                            'match',
                            ['get', 'net_func'],
                            'TRUNK',
                            '#FF6C00',
                            'DISTRIBUTION',
                            '#00B9A5',
                            'COMMUNICATION PIPE',
                            '#FFA500',
                            '#70A2FD',
                        ],
                        // 8px once fittings are shown (semantic Level 1, zoom ≥ 17); ramps down to
                        // 4px across the Pipelines level (zoom 16→17) so the dense network isn't
                        // overwhelmed before fittings appear. The hoverProgress feature-state (0→1,
                        // tweened in usePipelineInteractions) grows the hovered pipe by up to 100% on
                        // top of the base. Zoom interpolation must stay top-level (MapLibre forbids
                        // nesting a `zoom` expression), so the hover factor is applied inside each stop.
                        'line-width': [
                            'interpolate',
                            ['linear'],
                            ['zoom'],
                            16,
                            ['*', 4, ['+', 1, ['number', ['feature-state', 'hoverProgress'], 0]]],
                            17,
                            ['*', 8, ['+', 1, ['number', ['feature-state', 'hoverProgress'], 0]]],
                        ],
                    },
                },
                'lineOverlays',
            );
        } catch {
            // Progressive enhancement — layer failures must not break the map.
        }
    }
}

/**
 * Loads pipeline GeoJSON for the selected client and current map viewport, registers
 * the MapLibre GeoJSON source and two line layers (drop-shadow case + Origin Blue main), and re-registers
 * them after every base map style change. Reloads data on map moveend.
 *
 * Visibility is set at registration time from `featureOverlays.pipelines`; subsequent
 * toggle changes are handled by `useMapLayerVisibility` via the populated
 * `pipelines.mapLayerIds`.
 *
 * Safe when:
 *   - No client is selected (clears source data immediately, skips fetch).
 *   - Active client changes (clears source immediately, then fetches for new client).
 *   - Pipeline data has not loaded yet (source contains empty FeatureCollection).
 *   - Map layers do not exist (all operations guard with getLayer/getSource).
 *   - API requests fail (error is surfaced via showErrorToast; map continues working).
 *   - Style reloads (re-registers using existing data; no additional API call).
 */
export function usePipelinesLayer(
    mapRef: RefObject<MapRef | null>,
    isMapLoaded: boolean,
    selectedClient: Client | null,
    featureOverlays: FeatureOverlayState,
): void {
    const pipelineDataRef = useRef<FeatureCollection>(EMPTY_FEATURE_COLLECTION);

    // Keep a ref so the registration effect can read the current overlay state
    // without re-running when featureOverlays changes (useMapLayerVisibility
    // owns subsequent visibility toggling via mapLayerIds).
    const featureOverlaysRef = useRef(featureOverlays);
    useLayoutEffect(() => {
        featureOverlaysRef.current = featureOverlays;
    });

    // Load pipeline data for the current viewport when client/map-ready/enabled state changes.
    // Re-loads on map moveend (covers both pan and zoom end).
    useEffect(() => {
        pipelineDataRef.current = EMPTY_FEATURE_COLLECTION;
        const mlMap = mapRef.current?.getMap();
        if (mlMap) setGeoJsonSourceData(mlMap, PIPELINE_SOURCE_ID, EMPTY_FEATURE_COLLECTION);

        if (!selectedClient || !isMapLoaded) return;
        if (!featureOverlays.pipelines) return;

        return attachViewportLoader(mapRef, {
            sourceId: PIPELINE_SOURCE_ID,
            minZoom: PIPELINES_MIN_ZOOM,
            clientId: selectedClient.id,
            dataRef: pipelineDataRef,
            fetchFn: fetchPipelinesForViewport,
        });
    }, [selectedClient, isMapLoaded, featureOverlays.pipelines, mapRef]);

    // Register pipeline source and layers when the map is ready, and re-register on
    // every style.load (base map switches reload all sources and layers).
    // Uses existing pipelineDataRef — no additional API call on style reload.
    useEffect(() => {
        if (!isMapLoaded) return;
        const mlMap = mapRef.current?.getMap();
        if (!mlMap) return;

        const register = () => {
            registerPipelineSourceAndLayers(mlMap, pipelineDataRef.current, featureOverlaysRef.current.pipelines);
        };

        register();
        mlMap.on('style.load', register);

        return () => {
            mlMap.off('style.load', register);
        };
    }, [isMapLoaded, mapRef]);
}
